// Add this to your AuthService or create a new tokenManager.js

class TokenManager {
    constructor() {
        this.token = null;
        this.profile = null;
        this.isLoaded = false;

        // Load initial data
        this.loadFromStorage();

        // Set up periodic sync
        this.setupPeriodicSync();

        // Monitor for extension interference
        this.setupExtensionDetection();
    }

    loadFromStorage() {
        try {
            this.token = localStorage.getItem('token');
            this.profile = localStorage.getItem('profile');
            this.isLoaded = true;
            console.log('TokenManager: Loaded from storage:', {
                hasToken: !!this.token,
                hasProfile: !!this.profile
            });
        } catch (error) {
            console.error('TokenManager: Error loading from storage:', error);
        }
    }

    getToken() {
        // Always check localStorage first, but fall back to memory
        const storageToken = localStorage.getItem('token');
        if (storageToken) {
            this.token = storageToken;
            return storageToken;
        }

        // If localStorage is empty but we have it in memory, restore it
        if (this.token && !storageToken) {
            console.warn('TokenManager: Token missing from localStorage, restoring from memory');
            this.setToken(this.token);
            return this.token;
        }

        return null;
    }

    setToken(token) {
        this.token = token;
        try {
            localStorage.setItem('token', token);
        } catch (error) {
            console.error('TokenManager: Error setting token in localStorage:', error);
        }
    }

    getProfile() {
        const storageProfile = localStorage.getItem('profile');
        if (storageProfile) {
            this.profile = storageProfile;
            return storageProfile;
        }

        if (this.profile && !storageProfile) {
            console.warn('TokenManager: Profile missing from localStorage, restoring from memory');
            this.setProfile(this.profile);
            return this.profile;
        }

        return null;
    }

    setProfile(profile) {
        this.profile = profile;
        try {
            localStorage.setItem('profile', profile);
        } catch (error) {
            console.error('TokenManager: Error setting profile in localStorage:', error);
        }
    }

    clear() {
        this.token = null;
        this.profile = null;
        try {
            localStorage.removeItem('token');
            localStorage.removeItem('profile');
        } catch (error) {
            console.error('TokenManager: Error clearing localStorage:', error);
        }
    }

    setupPeriodicSync() {
        // Sync memory with localStorage every 1 second
        setInterval(() => {
            const storageToken = localStorage.getItem('token');
            const storageProfile = localStorage.getItem('profile');

            // If localStorage is empty but we have data in memory, restore it
            if (this.token && !storageToken) {
                console.warn('TokenManager: Restoring token to localStorage');
                localStorage.setItem('token', this.token);
            }

            if (this.profile && !storageProfile) {
                console.warn('TokenManager: Restoring profile to localStorage');
                localStorage.setItem('profile', this.profile);
            }
        }, 1000);
    }

    setupExtensionDetection() {
        let lastToken = localStorage.getItem('token');

        setInterval(() => {
            const currentToken = localStorage.getItem('token');

            if (lastToken && !currentToken && this.token) {
                console.warn('TokenManager: Browser extension cleared localStorage, restoring...');
                this.setToken(this.token);
                this.setProfile(this.profile);
            }

            lastToken = currentToken;
        }, 500); // Check every 500ms
    }

    isAuthenticated() {
        return !!this.getToken();
    }
}

// Create singleton instance
const tokenManager = new TokenManager();

// Updated AuthService methods
export const getAuthToken = () => {
    return tokenManager.getToken();
};

export const setAuthToken = (token) => {
    tokenManager.setToken(token);
};

export const getAuthProfile = () => {
    return tokenManager.getProfile();
};

export const setAuthProfile = (profile) => {
    tokenManager.setProfile(typeof profile === 'string' ? profile : JSON.stringify(profile));
};

export const clearAuth = () => {
    tokenManager.clear();
};

export const isAuthenticated = () => {
    return tokenManager.isAuthenticated();
};

export default tokenManager;